import { action, computed, observable } from 'mobx';

import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import history from 'bz-utilities/modules/networking/history/rr4';
import { ICON_POSITION } from 'click-controls/constant/enums';
import Button from 'click-controls/controls/form/button';
import Field from 'click-controls/controls/form/field';
import { observer } from 'mobx-react';
import QueryString from 'query-string';
import React from 'react';
import { Link } from 'react-router-dom';
import { cache } from 'tm3-core/cache';
import { WhatIsAPassKey } from '../2fa/setup/WhatIsAPassKey';
import LoginStore from './Store';
import Error from './controls/error';

const emailFilter = /[A-Za-z]|\d|\.|\-|\@|\_|\+/;

interface IProps {}
@observer
class Login extends React.Component<IProps> {
  displayName = 'LoginPanel';
  loginStore: LoginStore;
  @observable userName = '';
  @observable password = '';
  @observable showPassword = false;
  @observable error = '';

  constructor(props: IProps) {
    super(props);
    this.loginStore = new LoginStore();
  }

  @action.bound
  handleIsAutoFill = (autofill: boolean) => {
    this.isAutoFill = autofill;
  };

  @action.bound
  async onLoginWithFido(e: React.ChangeEvent<HTMLButtonElement>) {
    e.preventDefault();
    await this.loginStore
      .onFidoLogin()
      .then((res: boolean) => {
        if (!res) return;
        const { redirectTo } = QueryString.parse(window.location.search);
        if (!!redirectTo) {
          history.push(String(redirectTo));
        } else {
          history.push('/');
        }
      })
      .catch((err) => {
        this.error = err;
      });
  }

  @action.bound
  login(e: React.ChangeEvent<HTMLButtonElement>) {
    e.preventDefault();
    const userName = this.userName;
    const password = this.password;
    return this.loginStore
      .login({ userName, password })
      .then((res: boolean) => {
        if (!res) return;
        const { redirectTo } = QueryString.parse(window.location.search);
        if (!!redirectTo) {
          history.push(String(redirectTo));
        } else {
          history.push('/');
        }
      })
      .catch((err) => {
        this.error = err;
      });
  }

  @action.bound
  usernameChange(e: React.ChangeEvent<HTMLInputElement>) {
    this.userName = e.target.value;
  }

  @action.bound
  passwordChange(e: React.ChangeEvent<HTMLInputElement>) {
    this.password = e.target.value;
  }

  @action.bound
  toggleShowPassword(e: React.MouseEvent<HTMLElement>) {
    e.preventDefault();
    this.showPassword = this.showPassword ? false : true;
  }

  @computed get canDoPasswordless() {
    return typeof PublicKeyCredential != 'undefined';
  }

  render() {
    const { canDoPasswordless, showPassword } = this;
    const { loginMode } = cache.app;
    if (loginMode === '/api/auth/ms-graph')
      return (
        <div className="mt-2 flex flex-col">
          <span className="pb-3 text-base">
            This system is setup to authenicate using Microsoft Active Directory.
          </span>
          <button
            className={`btn mt-1 !bg-indigo-500 hover:!bg-indigo-600`}
            onClick={() =>
              (window.location = `${cache.app.loginMode}?redirect=${window.location.origin}`)
            }
          >
            Sign In with Microsoft
          </button>
        </div>
      );
    return (
      <>
        <form
          id="form-login"
          onKeyUp={(event: any) => {
            if (event.key == 'Enter') this.login(event);
          }}
        >
          <h3 className="mb-4 text-3xl font-bold">Sign in to your account</h3>

          <Field
            type="email"
            label="Email Address"
            placeholder="abc@example.com"
            required={true}
            onChange={this.usernameChange.bind(this)}
            validationRegEx={emailFilter}
          />
          <Field
            required={true}
            type={showPassword ? 'text' : 'password'}
            label="Password"
            icon={
              <div
                tabIndex={-1}
                onClick={this.toggleShowPassword}
                className="h-5 w-5 cursor-pointer bg-white stroke-gray-600 hover:stroke-gray-800"
              >
                {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
              </div>
            }
            iconPosition={ICON_POSITION.Right}
            onChange={this.passwordChange.bind(this)}
          />
          <p className="mb-6 text-left text-sm">
            <Link to="/login/help">Forgot your password?</Link>
          </p>
          <Error message={this.error} />
          <div className="mt-2 grid">
            <Button onClick={this.login.bind(this)}>Sign In</Button>
          </div>
        </form>
        <div className="mt-2 flex flex-col">
          {canDoPasswordless && (
            <button
              className={`btn mt-1 ${
                !!process.env.isClick
                  ? '!bg-clickPink hover:!bg-clickPink'
                  : '!bg-emerald-500 hover:!bg-emerald-600'
              }`}
              onClick={this.onLoginWithFido}
            >
              Sign In with Passkey
            </button>
          )}
          {this.loginStore.fidoStore.message && (
            <details className="my-2 rounded-md border border-rose-300 bg-rose-100 p-3 text-center text-sm text-rose-600">
              <summary>
                <span>There was an error signing in by Passkey</span>
              </summary>
              <p className="mt-2">{this.loginStore.fidoStore.message}</p>
            </details>
          )}
          <WhatIsAPassKey />
        </div>
      </>
    );
  }
}

/**
 * Sends the credential to the the FIDO2 server for assertion
 * @param {any} assertedCredential

async function verifyAssertionWithServer(assertedCredential) {

    // Move data into Arrays incase it is super long
    let authData = new Uint8Array(assertedCredential.response.authenticatorData);
    let clientDataJSON = new Uint8Array(assertedCredential.response.clientDataJSON);
    let rawId = new Uint8Array(assertedCredential.rawId);
    let sig = new Uint8Array(assertedCredential.response.signature);
    let userHandle = new Uint8Array(assertedCredential.response.userHandle)
    const data = {
        id: assertedCredential.id,
        rawId: coerceToBase64Url(rawId),
        type: assertedCredential.type,
        extensions: assertedCredential.getClientExtensionResults(),
        response: {
            authenticatorData: coerceToBase64Url(authData),
            clientDataJSON: coerceToBase64Url(clientDataJSON),
            userHandle: userHandle !== null ? coerceToBase64Url(userHandle): null,
            signature: coerceToBase64Url(sig)
        }
    };

    let response;
    try {
        let res = await fetch("/makeAssertion", {
            method: 'POST', // or 'PUT'
            body: JSON.stringify(data), // data can be `string` or {object}!
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        });

        response = await res.json();
    } catch (e) {
        showErrorAlert("Request to server failed", e);
        throw e;
    }

    console.log("Assertion Object", response);

    // show error
    if (response.status !== "ok") {
        console.log("Error doing assertion");
        console.log(response.errorMessage);
        showErrorAlert(response.errorMessage);
        return;
    }

    // show success message
    Swal.fire({
        title: 'Logged In!',
        text: 'You\'re logged in successfully.',
        type: 'success',
        timer: 2000
    });

    // redirect?
    //window.location.href = "/dashboard/" + state.user.displayName;
}


*/
export default Login;
